import {
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryInvoicingConfigForPayee } from "../../../../query/invoicingConfig/queries/useQueryInvoicingConfigForPayee";
import { useGetPayerEngagementQuery } from "../../../../query/payerEngagements/queries/useGetPayerEngagementQuery";
import { usePayerQuery } from "../../../../query/payers/queries/usePayerQuery";
import { useQueryPayerRows } from "../../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryEngagementsWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryEngagementWorkDefinitions";
import { useQueryWorkItems } from "../../../../query/workItem/queries/useQueryWorkItems";
import { useQueryWorkLog } from "../../../../query/workLog/queries/useQueryWorkLog";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { getPayerName } from "../../../Payers/utils/getPayerNames";
import { AlertWorkLogInvoiceStatusPayee } from "../../components/AlertWorkLogInvoiceStatusPayee";
import { CardWorkItems } from "../../components/CardWorkItems";
import { WorkLogAdditionalDetailsPayeeView } from "../../components/WorkLogAdditionalDetailsPayeeView";
import { WorkLogAssignmentInfoPayeeView } from "../../components/WorkLogAssignmentInfoPayeeView";
import { WorkLogStatusStepperPayeeView } from "../../components/WorkLogStatusStepperPayeeView";
import { useModalConvert } from "../../modals/ModalConvert";
import { PATH_WORK_LOGS_PAYEE } from "../../paths";

export const RoutePayeeWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const userId = useUserId();
  const history = useHistory();

  // Query work log
  const queryWorkLog = useQueryWorkLog(workLogId);
  const workLog = queryWorkLog.data;

  // Query payer information
  const queryPayerRow = useQueryPayerRows(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payerPayeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payerPayeeEngagementId
    }
  );

  const payerId = queryPayerRow.data?.[0]?.payerId;
  const payerPayeeEngagementId = workLog?.payerPayeeEngagementId;

  const queryPayer = usePayerQuery(payerId!, {
    enabled: !!payerId
  });

  const queryPayerPayeeEngagement = useGetPayerEngagementQuery(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );

  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Query work definitions
  const queryWorkDefinitions = useQueryEngagementsWorkDefinitions(
    {
      payerId: payerId!,
      engagementId: payerPayeeEngagementId!
    },
    {
      enabled: !!(payerId && payerPayeeEngagementId)
    }
  );

  // Query invoicing config
  const queryInvoicingConfigForPayee = useQueryInvoicingConfigForPayee(
    payerId as string,
    {
      enabled: !!payerId
    }
  );

  // Query work items
  const queryWorkItems = useQueryWorkItems({
    "filter[workLogId][=]": workLogId
  });

  // Modal for converting to invoice
  const modalConvert = useModalConvert();

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Work Log Details";
    }

    if (!payer) {
      return "Work Log Details";
    }

    const payerName = getPayerName(payer);

    if (payerPayeeEngagement) {
      return `Work Log – ${payerName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Work Log – ${payerName}`;
  }, [payer, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Action handlers
  const handleViewInvoice = useCallback(() => {
    history.push(`/member/invoices/${workLog?.invoiceId}`);
  }, [workLog?.invoiceId, history]);

  const handleCreateInvoice = useCallback(() => {
    modalConvert.open({
      workLogId,
      onClose: result => {
        if (result && !result.createError) {
          queryWorkLog.refetch();
        }
      }
    });
  }, [modalConvert, workLogId, queryWorkLog]);

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          type="search"
          title="Work log not found"
          description="The work log you're looking for doesn't exist or has been deleted."
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
              }
            ]
          }}
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            type="error"
            title="Access denied"
            description="You don't have permission to view this work log."
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
                }
              ]
            }}
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
        breadcrumbs={[
          {
            label: "Work logs",
            onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
          },
          {
            label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
          }
        ]}
        actions={[
          {
            label: "View invoice",
            buttonKind: "Primary",
            onClick: handleViewInvoice,
            hidden: workLog?.status === WorkLogStatus.Draft
          },
          {
            label: "Create invoice",
            buttonKind: "Primary",
            onClick: handleCreateInvoice,
            disabled: (queryWorkItems.data || []).length === 0,
            hidden: workLog?.status === WorkLogStatus.Approved
          }
        ]}
      />

      <AlertWorkLogInvoiceStatusPayee workLog={workLog} />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayeeView
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
              payer={payer}
              payerPayeeEngagement={payerPayeeEngagement}
            />

            <CardWorkItems
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryWorkDefinitions.isInitialLoading
              }
              noEdit={workLog?.status === WorkLogStatus.Approved}
              workLogId={workLogId}
              payerId={payerId!}
              payerPayeeEngagementId={workLog?.payerPayeeEngagementId!}
              workItems={queryWorkItems.data || []}
              workDefinitions={queryWorkDefinitions.data || []}
              resources={{ payerPayeeEngagement }}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayeeView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogAdditionalDetailsPayeeView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
              payerId={payerId}
              payeeId={userId}
            />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
